/**
 * DelegationBanner — SHELL-DLG-001: Persistent delegation session indicator.
 *
 * Rendered by ApplicationShell when the active context is operating in
 * delegated support mode (`currentContext.delegated === true`).
 *
 * Requirements:
 * - Visible on every page in the shell while a delegation is active.
 * - Displays the target organization name.
 * - Provides a one-click exit action that switches
 *   the user back to their own non-delegated context.
 * - Falls back to logout if no non-delegated context is available.
 * - Uses aria-label and role="status" for screen-reader accessibility.
 * - Never infers delegation state from role names; reads from session context.
 */
import { useCallback, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTE_IDS } from '@finance/contracts';
import { getRouteById } from '../../routes/registry';
import { useSession } from '../../features/auth/session-provider';
import { useLogout } from '../../features/auth/useLogout';

export function DelegationBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentContext, availableContexts, switchContext, exitDelegation } = useSession();
  const logout = useLogout();
  const [exiting, setExiting] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);

  const handleExit = useCallback(async () => {
    setExiting(true);
    setExitError(null);
    try {
      if (exitDelegation) {
        await exitDelegation();
        return;
      }

      // Legacy fallback for test doubles and older session providers.
      const ownContext = availableContexts.find(
        (ctx) =>
          ctx.membershipId !== currentContext?.membershipId ||
          ctx.branchId !== currentContext?.branchId,
      );

      if (ownContext && ownContext.branchId) {
        await switchContext(ownContext.membershipId, ownContext.branchId);
      } else {
        // No own context available — log out entirely.
        const loginPath = getRouteById(ROUTE_IDS.LOGIN)?.path ?? '/login';
        try {
          await logout.mutateAsync();
        } catch {
          // Local cleanup is authoritative; a failed remote revocation must not
          // trap the user in the delegated session.
        }
        navigate(loginPath, { replace: true });
      }
    } catch {
      setExitError(t('shell:contextSelector.switchError'));
    } finally {
      setExiting(false);
    }
  }, [availableContexts, currentContext, switchContext, exitDelegation, logout, navigate, t]);

  // Only render when the current session is delegated.
  if (!currentContext?.delegated) return null;

  const organizationName = currentContext.organizationName;

  return (
    <Alert
      role="status"
      aria-label={t('shell:delegationBanner.ariaLabel')}
      severity="warning"
      icon={false}
      data-testid="delegation-banner"
      sx={{
        borderRadius: 0,
        py: 0.5,
        px: 2,
        '& .MuiAlert-message': {
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
          {t('shell:delegationBanner.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 1, minWidth: 0 }}
        >
          {t('shell:delegationBanner.organizationLabel')}:{' '}
          <strong>{organizationName}</strong>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {exitError && (
          <Typography variant="caption" color="error.main">
            {exitError}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={exiting ? <CircularProgress size={14} color="inherit" /> : <ExitToAppIcon fontSize="small" />}
          disabled={exiting}
          onClick={() => void handleExit()}
          aria-label={t('shell:delegationBanner.exitAriaLabel')}
          data-testid="delegation-banner-exit"
        >
          {t('shell:delegationBanner.exitLabel')}
        </Button>
      </Box>
    </Alert>
  );
}
